import { COMPANY_FILTER_CHANGE } from 'containers/PartnerOptions/constants'

import {
  FETCH_KILLSWITCH_ASYNC,
  FETCH_ORIGINAL_SUBJECT_ASYNC,
  FETCH_SETTINGS_ASYNC,
  SAVE_SETTINGS_ASYNC,
  UPDATE_KILLSWITCH_ASYNC,
  UPDATE_ORIGINAL_SUBJECT_ASYNC,
  UPDATE_SILENT_MODE_ASYNC,
  UPDATE_STATUS_ASYNC,
} from './constants'

const initialKillswitchState = {
  active: null,
  state: null,
  operation: null,
  affectedRulesCount: null,
  loader: false,
  error: null,
  loaded: false,
}

const applyKillswitchSnapshot = (prev, killswitchData = {}) => {
  const snapshotState =
    killswitchData.state ||
    (killswitchData.active === true || killswitchData.active === false ? 'ready' : null)
  const isFailed = snapshotState === 'failed'
  const isReady = snapshotState === 'ready'
  const hasTerminalStatus = isReady || isFailed
  // active is only trustworthy once the operation has finished.
  const nextActive =
    isReady && (killswitchData.active === true || killswitchData.active === false)
      ? killswitchData.active
      : prev.active

  return {
    ...prev,
    active: nextActive,
    state: snapshotState,
    operation: killswitchData.operation ?? null,
    affectedRulesCount: killswitchData.affectedRulesCount ?? null,
    // Keep the first-load spinner until we get a terminal snapshot.
    loader: hasTerminalStatus ? false : !prev.loaded,
    error: isFailed ? killswitchData.error || { detail: 'Killswitch operation failed' } : null,
    loaded: prev.loaded || hasTerminalStatus,
  }
}

const initialState = {
  data: null,
  entitlement: null,
  loader: false,
  error: null,
  saving: false,
  saveSuccess: false,
  powershellEnabled: null,
  killswitch: initialKillswitchState,
  killswitchUpdating: false,
  killswitchUpdateError: null,
  silentModeUpdating: false,
  silentModeUpdateError: null,
  useOriginalSubject: false,
  originalSubjectLoaded: false,
  originalSubjectLoadError: null,
  originalSubjectUpdating: false,
  originalSubjectUpdateError: null,
}

const extractConfigPayload = (payloadData = {}) => {
  const { _entitlement, _powershellEnabled, ...configData } = payloadData

  return { _entitlement, _powershellEnabled, configData }
}

export default function emailEncryptionSettingsReducer(state = initialState, action) {
  switch (action.type) {
    case COMPANY_FILTER_CHANGE:
      return {
        ...initialState,
        loader: true,
      }

    case FETCH_SETTINGS_ASYNC.INITIATE:
      return {
        ...state,
        loader: true,
        error: null,
      }

    case UPDATE_STATUS_ASYNC.INITIATE:
      return { ...state, loader: true, error: null }

    case FETCH_SETTINGS_ASYNC.SUCCESS: {
      const { _entitlement, _powershellEnabled, configData } = extractConfigPayload(
        action.payload.data || {}
      )

      return {
        ...state,
        data: configData,
        entitlement: _entitlement ?? state.entitlement,
        powershellEnabled: _powershellEnabled ?? null,
        loader: false,
        error: null,
      }
    }

    case UPDATE_STATUS_ASYNC.SUCCESS:
      return { ...state, data: action.payload.data, loader: false, error: null }

    case FETCH_SETTINGS_ASYNC.ERROR:
    case UPDATE_STATUS_ASYNC.ERROR:
      return { ...state, error: action.error, loader: false }

    case UPDATE_SILENT_MODE_ASYNC.INITIATE:
      return { ...state, silentModeUpdating: true, silentModeUpdateError: null }

    case UPDATE_SILENT_MODE_ASYNC.SUCCESS: {
      const nextData = action.payload.data?.data ?? action.payload.data ?? {}
      const dlpSilentMode = nextData.dlpSilentMode === true

      return {
        ...state,
        entitlement: {
          ...state.entitlement,
          dlpSilentMode,
          ...(nextData.encryptionEntitled !== undefined && {
            encryptionEntitled: nextData.encryptionEntitled,
          }),
          ...(nextData.encryptionActive !== undefined && {
            encryptionActive: nextData.encryptionActive,
          }),
          ...(nextData.dlpEntitled !== undefined && { dlpEntitled: nextData.dlpEntitled }),
          ...(nextData.dlpActive !== undefined && { dlpActive: nextData.dlpActive }),
        },
        silentModeUpdating: false,
        silentModeUpdateError: null,
      }
    }

    case UPDATE_SILENT_MODE_ASYNC.ERROR:
      return {
        ...state,
        silentModeUpdating: false,
        silentModeUpdateError: action.error,
      }

    case SAVE_SETTINGS_ASYNC.INITIATE:
      return { ...state, saving: true, saveSuccess: false, error: null }

    case SAVE_SETTINGS_ASYNC.SUCCESS:
      return { ...state, data: action.payload.data, saving: false, saveSuccess: true }

    case SAVE_SETTINGS_ASYNC.ERROR:
      return { ...state, saving: false, saveSuccess: false, error: action.error }

    case FETCH_KILLSWITCH_ASYNC.INITIATE: {
      const isInitialLoad = !state.killswitch.loaded
      return {
        ...state,
        killswitch: {
          ...state.killswitch,
          // Show loader only on the first fetch; later polls update silently.
          loader: isInitialLoad,
          error: isInitialLoad ? null : state.killswitch.error,
        },
      }
    }

    case FETCH_KILLSWITCH_ASYNC.SUCCESS: {
      const killswitchData = action.payload.data?.data ?? action.payload.data
      const killswitch = applyKillswitchSnapshot(state.killswitch, killswitchData)
      const inFlight = killswitch.state === 'pending' || killswitch.state === 'running'
      const isFailed = killswitch.state === 'failed'
      const keptUpdateError = inFlight ? state.killswitchUpdateError : null
      return {
        ...state,
        killswitch,
        killswitchUpdating: inFlight,
        killswitchUpdateError: isFailed ? killswitch.error : keptUpdateError,
      }
    }

    case FETCH_KILLSWITCH_ASYNC.ERROR:
      return {
        ...state,
        killswitch: {
          ...state.killswitch,
          loader: false,
          error: action.error,
          // Keep last known status visible across silent poll failures.
          loaded: state.killswitch.loaded,
        },
      }

    case UPDATE_KILLSWITCH_ASYNC.INITIATE:
      return {
        ...state,
        killswitchUpdating: true,
        killswitchUpdateError: null,
      }

    case UPDATE_KILLSWITCH_ASYNC.SUCCESS: {
      const { requestedAction, ...ack } = action.payload.data?.data ?? action.payload.data ?? {}
      // Without a snapshot the ack means "job accepted", never "job done".
      const ackSnapshot = ack.state
        ? ack
        : {
            ...ack,
            active: undefined,
            state: 'pending',
            operation: ack.operation ?? requestedAction,
          }
      const killswitch = applyKillswitchSnapshot(state.killswitch, ackSnapshot)
      const inFlight = killswitch.state === 'pending' || killswitch.state === 'running'
      const isFailed = killswitch.state === 'failed'
      return {
        ...state,
        killswitch,
        killswitchUpdating: inFlight,
        killswitchUpdateError: isFailed ? killswitch.error : null,
      }
    }

    case UPDATE_KILLSWITCH_ASYNC.ERROR:
      return {
        ...state,
        killswitchUpdating: false,
        killswitchUpdateError: action.error,
      }

    case FETCH_ORIGINAL_SUBJECT_ASYNC.INITIATE:
      return { ...state, originalSubjectLoadError: null }

    case FETCH_ORIGINAL_SUBJECT_ASYNC.SUCCESS:
      return {
        ...state,
        useOriginalSubject: action.payload.data === true,
        originalSubjectLoaded: true,
        originalSubjectLoadError: null,
      }

    case FETCH_ORIGINAL_SUBJECT_ASYNC.ERROR:
      return { ...state, originalSubjectLoaded: false, originalSubjectLoadError: action.error }

    case UPDATE_ORIGINAL_SUBJECT_ASYNC.INITIATE:
      return { ...state, originalSubjectUpdating: true, originalSubjectUpdateError: null }

    case UPDATE_ORIGINAL_SUBJECT_ASYNC.SUCCESS:
      return {
        ...state,
        useOriginalSubject: action.payload.data === true,
        originalSubjectUpdating: false,
        originalSubjectUpdateError: null,
      }

    case UPDATE_ORIGINAL_SUBJECT_ASYNC.ERROR:
      return {
        ...state,
        originalSubjectUpdating: false,
        originalSubjectUpdateError: action.error,
      }

    default:
      return state
  }
}
